import {
    regenerateShopBlockUuids,
} from "../blockIdentity";
import {
    SHOP_DOCUMENT_ROLE_OPTION,
    SHOP_EDITOR_ORIGIN_OPTION,
    SHOP_EDITOR_ORIGIN_USER,
    SHOP_GRID_SLOT_KEY,
} from "../shopBlocks";
import {
    SITE_NAVIGATION_MENU_LOCATION_OPTION,
    SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION,
    resolveSiteNavigationMenu,
    resolveSiteNavigationSettings,
    siteNavigationMenuLocationCandidates,
} from "../siteNavigation";
import type { SiteNavigationSettings } from "../siteNavigation";
import type {
    PublicShopBasicData,
    PublicShopMenu,
    ShopTemplateBlock,
} from "../types";

export const SHOP_HEADER_REQUIRED_ROLES = [
    "headerLogo",
    "headerMenu",
] as const;

export const SHOP_HEADER_OPTIONAL_ROLES = [
    "headerSearch",
    "headerStoresButton",
    "headerAccountButton",
    "headerLanguageSelector",
    "headerCurrencySelector",
    "headerFavoritesButton",
    "headerCartButton",
] as const;

export const SHOP_HEADER_ROLES = [
    "headerLogo",
    "headerSearch",
    "headerStoresButton",
    "headerAccountButton",
    "headerLanguageSelector",
    "headerCurrencySelector",
    "headerFavoritesButton",
    "headerCartButton",
    "headerMenu",
] as const;

export const SHOP_HEADER_UTILITY_ROLES = [
    "headerStoresButton",
    "headerAccountButton",
    "headerLanguageSelector",
    "headerCurrencySelector",
    "headerFavoritesButton",
    "headerCartButton",
] as const;

export type ShopHeaderRole = typeof SHOP_HEADER_ROLES[number];
export type ShopHeaderRequiredRole = typeof SHOP_HEADER_REQUIRED_ROLES[number];
export type ShopHeaderOptionalRole = typeof SHOP_HEADER_OPTIONAL_ROLES[number];
export type ShopHeaderUtilityRole = typeof SHOP_HEADER_UTILITY_ROLES[number];
export type ShopHeaderRootBlock = ShopTemplateBlock & {
    type: "header" | "richContent";
};

export const SHOP_HEADER_UTILITY_DISPLAY_OPTION = "utilityDisplay";
export const SHOP_HEADER_SHOW_ON_DESKTOP_OPTION = "showOnDesktop";
export const SHOP_HEADER_SHOW_ON_MOBILE_OPTION = "showOnMobile";
export const SHOP_HEADER_ALIGNMENT_OPTION = "alignment";
export const SHOP_HEADER_LOGO_DISPLAY_OPTION = "logoDisplay";
/** @deprecated Prefer SITE_NAVIGATION_MENU_LOCATION_OPTION. */
export const SHOP_HEADER_MENU_LOCATION_OPTION = SITE_NAVIGATION_MENU_LOCATION_OPTION;
/** @deprecated Prefer SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION. */
export const SHOP_HEADER_MOBILE_MENU_LOCATION_OPTION = SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION;

export const SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS = {
    desktop: ["catalog", "header", "main"],
    mobile: ["mobile", "catalog", "header", "main"],
} as const;

export const SHOP_HEADER_UTILITY_DISPLAYS = [
    "icon",
    "label",
    "iconLabel",
] as const;

export const SHOP_HEADER_ALIGNMENTS = [
    "left",
    "center",
    "right",
] as const;

export const SHOP_HEADER_LOGO_DISPLAYS = [
    "markText",
    "mark",
    "text",
] as const;

export const SHOP_HEADER_VIEWPORTS = [
    "desktop",
    "mobile",
] as const;

export const SHOP_HEADER_SEARCH_SIZES = [
    "compact",
    "standard",
    "wide",
] as const;

export const SHOP_HEADER_SEARCH_WIDTHS = {
    compact: 3,
    standard: 4,
    wide: 5,
} as const;

export type ShopHeaderUtilityDisplay = typeof SHOP_HEADER_UTILITY_DISPLAYS[number];
export type ShopHeaderAlignment = typeof SHOP_HEADER_ALIGNMENTS[number];
export type ShopHeaderLogoDisplay = typeof SHOP_HEADER_LOGO_DISPLAYS[number];
export type ShopHeaderViewport = typeof SHOP_HEADER_VIEWPORTS[number];
export type ShopHeaderSearchSize = typeof SHOP_HEADER_SEARCH_SIZES[number];
export type ShopHeaderSearchWidth = typeof SHOP_HEADER_SEARCH_WIDTHS[ShopHeaderSearchSize];

export const SHOP_HEADER_LAYOUT_PRESETS = [
    "balanced",
    "searchFirst",
    "centeredBrand",
    "compact",
] as const;

export type ShopHeaderLayoutPreset = typeof SHOP_HEADER_LAYOUT_PRESETS[number];

export type ShopHeaderGridPlacement = {
    h: number;
    w: number;
    x: number;
    y: number;
};

export const SHOP_HEADER_LAYOUT_PLACEMENTS: Readonly<Record<
    ShopHeaderLayoutPreset,
    Readonly<Record<ShopHeaderRole, ShopHeaderGridPlacement>>
>> = {
    balanced: {
        headerLogo: { x: 1, y: 1, w: 3, h: 1 },
        headerSearch: { x: 4, y: 1, w: 3, h: 1 },
        headerStoresButton: { x: 7, y: 1, w: 1, h: 1 },
        headerAccountButton: { x: 8, y: 1, w: 1, h: 1 },
        headerLanguageSelector: { x: 9, y: 1, w: 1, h: 1 },
        headerCurrencySelector: { x: 10, y: 1, w: 1, h: 1 },
        headerFavoritesButton: { x: 11, y: 1, w: 1, h: 1 },
        headerCartButton: { x: 12, y: 1, w: 1, h: 1 },
        headerMenu: { x: 1, y: 2, w: 12, h: 2 },
    },
    searchFirst: {
        headerSearch: { x: 1, y: 1, w: 4, h: 1 },
        headerLogo: { x: 5, y: 1, w: 2, h: 1 },
        headerStoresButton: { x: 7, y: 1, w: 1, h: 1 },
        headerAccountButton: { x: 8, y: 1, w: 1, h: 1 },
        headerLanguageSelector: { x: 9, y: 1, w: 1, h: 1 },
        headerCurrencySelector: { x: 10, y: 1, w: 1, h: 1 },
        headerFavoritesButton: { x: 11, y: 1, w: 1, h: 1 },
        headerCartButton: { x: 12, y: 1, w: 1, h: 1 },
        headerMenu: { x: 1, y: 2, w: 12, h: 2 },
    },
    centeredBrand: {
        headerLogo: { x: 4, y: 1, w: 6, h: 1 },
        headerSearch: { x: 1, y: 2, w: 6, h: 1 },
        headerStoresButton: { x: 7, y: 2, w: 1, h: 1 },
        headerAccountButton: { x: 8, y: 2, w: 1, h: 1 },
        headerLanguageSelector: { x: 9, y: 2, w: 1, h: 1 },
        headerCurrencySelector: { x: 10, y: 2, w: 1, h: 1 },
        headerFavoritesButton: { x: 11, y: 2, w: 1, h: 1 },
        headerCartButton: { x: 12, y: 2, w: 1, h: 1 },
        headerMenu: { x: 1, y: 3, w: 12, h: 2 },
    },
    compact: {
        headerLogo: { x: 1, y: 1, w: 3, h: 1 },
        headerSearch: { x: 4, y: 1, w: 5, h: 1 },
        headerStoresButton: { x: 9, y: 1, w: 1, h: 1 },
        headerAccountButton: { x: 10, y: 1, w: 1, h: 1 },
        headerFavoritesButton: { x: 11, y: 1, w: 1, h: 1 },
        headerCartButton: { x: 12, y: 1, w: 1, h: 1 },
        headerLanguageSelector: { x: 1, y: 2, w: 1, h: 1 },
        headerCurrencySelector: { x: 2, y: 2, w: 1, h: 1 },
        headerMenu: { x: 1, y: 3, w: 12, h: 1 },
    },
};

type MorrowCompactCoreRole = "headerLogo" | "headerMenu" | "headerSearch";

const MORROW_COMPACT_CORE_LAYOUT: Readonly<Record<
    ShopHeaderLayoutPreset,
    readonly Readonly<{ role: MorrowCompactCoreRole; weight: number }>[]
>> = {
    balanced: [
        { role: "headerLogo", weight: 2 },
        { role: "headerMenu", weight: 3 },
        { role: "headerSearch", weight: 4 },
    ],
    searchFirst: [
        { role: "headerSearch", weight: 4 },
        { role: "headerMenu", weight: 3 },
        { role: "headerLogo", weight: 2 },
    ],
    centeredBrand: [
        { role: "headerSearch", weight: 2 },
        { role: "headerMenu", weight: 3 },
        { role: "headerLogo", weight: 4 },
    ],
    compact: [
        { role: "headerLogo", weight: 1 },
        { role: "headerMenu", weight: 2 },
        { role: "headerSearch", weight: 4 },
    ],
};

const MORROW_COMPACT_INDEPENDENT_UTILITY_ROLES = [
    "headerStoresButton",
    "headerAccountButton",
    "headerFavoritesButton",
    "headerCartButton",
] as const satisfies readonly ShopHeaderUtilityRole[];

const MORROW_COMPACT_SELECTOR_ROLES = [
    "headerLanguageSelector",
    "headerCurrencySelector",
] as const satisfies readonly ShopHeaderUtilityRole[];

const SHOP_HEADER_LAYOUT_ROLE_ORDER: Readonly<Record<
    ShopHeaderLayoutPreset,
    readonly ShopHeaderRole[]
>> = {
    balanced: SHOP_HEADER_ROLES,
    searchFirst: [
        "headerSearch",
        "headerLogo",
        "headerStoresButton",
        "headerAccountButton",
        "headerLanguageSelector",
        "headerCurrencySelector",
        "headerFavoritesButton",
        "headerCartButton",
        "headerMenu",
    ],
    centeredBrand: SHOP_HEADER_ROLES,
    compact: [
        "headerLogo",
        "headerSearch",
        "headerStoresButton",
        "headerAccountButton",
        "headerFavoritesButton",
        "headerCartButton",
        "headerLanguageSelector",
        "headerCurrencySelector",
        "headerMenu",
    ],
};

const legacyRoleAliases: Readonly<Record<string, ShopHeaderRole>> = {
    accountAction: "headerAccountButton",
    brand: "headerLogo",
    cartAction: "headerCartButton",
};

const roleSet = new Set<string>(SHOP_HEADER_ROLES);
const requiredRoleSet = new Set<string>(SHOP_HEADER_REQUIRED_ROLES);
const utilityRoleSet = new Set<string>(SHOP_HEADER_UTILITY_ROLES);
const utilityDisplaySet = new Set<string>(SHOP_HEADER_UTILITY_DISPLAYS);
const alignmentSet = new Set<string>(SHOP_HEADER_ALIGNMENTS);
const logoDisplaySet = new Set<string>(SHOP_HEADER_LOGO_DISPLAYS);

const roleRuntimeOptions: Readonly<Record<ShopHeaderRole, Readonly<Record<string, unknown>>>> = {
    headerLogo: {
        commerceRuntimeRole: "commerce.header.logo",
        runtimeSource: "shopIdentity",
    },
    headerSearch: {
        commerceRuntimeRole: "commerce.header.search",
        runtimeSource: "headerSearch",
    },
    headerStoresButton: {
        commerceRuntimeRole: "commerce.header.stores",
        runtimeSource: "stores",
    },
    headerAccountButton: {
        commerceRuntimeRole: "commerce.header.account",
        runtimeSource: "customerAccount",
    },
    headerLanguageSelector: {
        commerceRuntimeRole: "commerce.header.language",
        runtimeSource: "languageSelector",
    },
    headerCurrencySelector: {
        commerceRuntimeRole: "commerce.header.currency",
        runtimeSource: "currencySelector",
    },
    headerFavoritesButton: {
        commerceRuntimeRole: "commerce.header.favorites",
        runtimeSource: "favorites",
    },
    headerCartButton: {
        commerceRuntimeRole: "commerce.header.cart",
        runtimeSource: "cart",
    },
    headerMenu: {
        commerceRuntimeRole: "commerce.header.menu",
        runtimeSource: "headerMenu",
    },
};

const roleDefaultVisibility: Readonly<Record<ShopHeaderRole, Readonly<Record<ShopHeaderViewport, boolean>>>> = {
    headerLogo: { desktop: true, mobile: true },
    headerSearch: { desktop: true, mobile: false },
    headerStoresButton: { desktop: true, mobile: false },
    headerAccountButton: { desktop: true, mobile: false },
    headerLanguageSelector: { desktop: true, mobile: false },
    headerCurrencySelector: { desktop: true, mobile: false },
    headerFavoritesButton: { desktop: true, mobile: true },
    headerCartButton: { desktop: true, mobile: true },
    headerMenu: { desktop: true, mobile: true },
};

type ShopHeaderRoleFactoryOptions = {
    content?: Record<string, unknown>;
    options?: Record<string, unknown>;
    slots?: Record<string, ShopTemplateBlock[]>;
};

export type ShopHeaderValidationIssue = {
    code: "duplicateRole" | "missingRequiredRole" | "notHeaderRoot";
    count: number;
    role: ShopHeaderRole | null;
};

function userOptions(options: Record<string, unknown>): Record<string, unknown> {
    return {
        ...options,
        [SHOP_EDITOR_ORIGIN_OPTION]: SHOP_EDITOR_ORIGIN_USER,
    };
}

function iconBlock(role: string, icon: string): ShopTemplateBlock {
    return {
        type: "icon",
        content: { icon },
        options: userOptions({
            [SHOP_DOCUMENT_ROLE_OPTION]: `${role}Icon`,
            iconSize: "28px",
            textColor: "var(--site-text)",
        }),
    };
}

function textBlock(
    role: string,
    text: string,
    messageKey: string,
    options: Record<string, unknown> = {},
): ShopTemplateBlock {
    return {
        type: "text",
        content: { text },
        defaultMessageKeys: { "content.text": messageKey },
        options: userOptions({
            [SHOP_DOCUMENT_ROLE_OPTION]: `${role}Label`,
            fontSize: "11px",
            fontWeight: "700",
            lineHeight: "1.1",
            textAlign: "center",
            textColor: "var(--site-text)",
            ...options,
        }),
    };
}

function defaultRoleContent(role: ShopHeaderRole): ShopTemplateBlock[] {
    if (role === "headerLogo") {
        return [textBlock(role, "Logo", "block.headerLogo.title", {
            fontSize: "24px",
            fontWeight: "900",
            letterSpacing: "-.04em",
            runtimeText: "shopName",
            textAlign: "left",
            textColor: "var(--site-primary)",
        })];
    }

    if (role === "headerSearch") {
        return [{
            type: "searchBar",
            content: { placeholder: "Co hledáte?" },
            defaultMessageKeys: { "content.placeholder": "block.headerSearch.placeholder" },
            options: userOptions({
                backgroundColor: "var(--site-surface)",
                borderColor: "var(--site-border)",
                borderRadius: "999px",
                borderWidth: "1px",
                [SHOP_DOCUMENT_ROLE_OPTION]: "headerSearchInput",
                minHeight: "46px",
            }),
            slots: {
                actionIcon: [{
                    type: "icon",
                    content: { icon: "search" },
                    options: userOptions({
                        backgroundColor: "var(--site-primary)",
                        borderRadius: "999px",
                        [SHOP_DOCUMENT_ROLE_OPTION]: "headerSearchActionIcon",
                        iconSize: "20px",
                        paddingBottom: "10px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        paddingTop: "10px",
                        textColor: "var(--site-on-primary)",
                    }),
                }],
            },
        }];
    }

    if (role === "headerMenu") {
        return [iconBlock(role, "navigation")];
    }

    if (role === "headerLanguageSelector") {
        return [
            iconBlock(role, "navigation"),
            textBlock(role, "Jazyk", "block.headerLanguageSelector.title", {
                runtimeText: "currentLanguageCode",
            }),
        ];
    }

    if (role === "headerCurrencySelector") {
        return [
            iconBlock(role, "creditCard"),
            textBlock(role, "Měna", "block.headerCurrencySelector.title", {
                runtimeText: "currentCurrencyCode",
            }),
        ];
    }

    const utility: Readonly<Record<
        Exclude<ShopHeaderUtilityRole, "headerLanguageSelector" | "headerCurrencySelector">,
        { icon: string; messageKey: string; text: string }
    >> = {
        headerStoresButton: {
            icon: "store",
            messageKey: "block.headerStoresButton.label",
            text: "Prodejny",
        },
        headerAccountButton: {
            icon: "user",
            messageKey: "block.headerAccountButton.label",
            text: "Účet",
        },
        headerFavoritesButton: {
            icon: "heart",
            messageKey: "block.headerFavoritesButton.label",
            text: "Oblíbené",
        },
        headerCartButton: {
            icon: "bag",
            messageKey: "block.headerCartButton.label",
            text: "Košík",
        },
    };
    const definition = utility[role as keyof typeof utility];

    return definition
        ? [
            iconBlock(role, definition.icon),
            textBlock(role, definition.text, definition.messageKey),
        ]
        : [];
}

function defaultUtilityDisplay(role: ShopHeaderRole): ShopHeaderUtilityDisplay | undefined {
    if (role === "headerLanguageSelector" || role === "headerCurrencySelector") {
        return "label";
    }

    if (role === "headerFavoritesButton" || role === "headerCartButton") {
        return "icon";
    }

    return utilityRoleSet.has(role) ? "iconLabel" : undefined;
}

function defaultRoleOptions(role: ShopHeaderRole): Record<string, unknown> {
    const placement = SHOP_HEADER_LAYOUT_PLACEMENTS.balanced[role];
    const utilityDisplay = defaultUtilityDisplay(role);
    const options: Record<string, unknown> = {
        alignment: role === "headerLogo" ? "left" : "center",
        builderAutoWrapped: true,
        builderTextColumn: true,
        contentLayout: role === "headerSearch" ? "vertical" : "inline",
        [SHOP_DOCUMENT_ROLE_OPTION]: role,
        gridH: placement.h,
        gridW: placement.w,
        gridX: placement.x,
        gridY: placement.y,
        [SHOP_HEADER_SHOW_ON_DESKTOP_OPTION]: roleDefaultVisibility[role].desktop,
        [SHOP_HEADER_SHOW_ON_MOBILE_OPTION]: roleDefaultVisibility[role].mobile,
        verticalAlign: "center",
        ...roleRuntimeOptions[role],
    };

    if (utilityDisplay) {
        options[SHOP_HEADER_UTILITY_DISPLAY_OPTION] = utilityDisplay;
        options.contentLayout = "vertical";
        options.gap = "4px";
        options.minHeight = "52px";
    }

    if (role === "headerLogo") {
        options[SHOP_HEADER_LOGO_DISPLAY_OPTION] = "markText";
    }

    if (role === "headerMenu") {
        options[SHOP_HEADER_MENU_LOCATION_OPTION] = SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS.desktop[0];
        options[SHOP_HEADER_MOBILE_MENU_LOCATION_OPTION] = SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS.mobile[0];
        options.gap = "28px";
        options.minHeight = "104px";
    }

    return userOptions(options);
}

function defaultRoleHref(role: ShopHeaderRole): string | null {
    if (role === "headerStoresButton") return "/prodejny";
    if (role === "headerAccountButton") return "/zakaznicky-ucet";
    if (role === "headerFavoritesButton") return "/oblibene";
    if (role === "headerCartButton") return "/kosik";

    return null;
}

export function isShopHeaderRole(value: unknown): value is ShopHeaderRole {
    return typeof value === "string" && roleSet.has(value);
}

export function canonicalShopHeaderRole(value: unknown): ShopHeaderRole | null {
    if (isShopHeaderRole(value)) {
        return value;
    }

    return typeof value === "string" ? legacyRoleAliases[value] ?? null : null;
}

export function shopHeaderRole(block: ShopTemplateBlock | null | undefined): ShopHeaderRole | null {
    if (!block) {
        return null;
    }

    return canonicalShopHeaderRole(
        block.options?.[SHOP_DOCUMENT_ROLE_OPTION]
        ?? (block.type === "headerMenu" ? "headerMenu" : null),
    );
}

export function isShopHeaderRoot(
    block: ShopTemplateBlock | null | undefined,
): block is ShopHeaderRootBlock {
    if (!block) {
        return false;
    }

    return block.type === "header"
        || (
            block.type === "richContent"
            && (
                block.options?.lagunaRegion === "header"
                || block.options?.morrowRegion === "header"
            )
        );
}

export function findShopHeaderRoot(block: ShopTemplateBlock | null | undefined): ShopTemplateBlock | null {
    if (!block) {
        return null;
    }

    if (isShopHeaderRoot(block)) {
        return block;
    }

    for (const children of Object.values(block.slots ?? {})) {
        for (const child of children) {
            const match = findShopHeaderRoot(child);

            if (match) {
                return match;
            }
        }
    }

    for (const child of block.children ?? []) {
        const match = findShopHeaderRoot(child);

        if (match) {
            return match;
        }
    }

    return null;
}

export function shopHeaderGrid(header: ShopTemplateBlock | null | undefined): readonly ShopTemplateBlock[] {
    return isShopHeaderRoot(header) ? header?.slots?.[SHOP_GRID_SLOT_KEY] ?? [] : [];
}

/** Ordered canonical-role view used by the header composer. Unknown authored
 * grid items remain in the real grid but are deliberately absent here. */
export function shopHeaderRoleBlocks(
    header: ShopTemplateBlock | null | undefined,
): Array<{ block: ShopTemplateBlock; role: ShopHeaderRole }> {
    return shopHeaderGrid(header).flatMap((block) => {
        const role = shopHeaderRole(block);

        return role ? [{ block, role }] : [];
    });
}

export function findShopHeaderRoleBlock(
    header: ShopTemplateBlock | null | undefined,
    role: ShopHeaderRole,
): ShopTemplateBlock | null {
    return shopHeaderGrid(header).find((block) => shopHeaderRole(block) === role) ?? null;
}

export function hasShopHeaderRole(
    header: ShopTemplateBlock | null | undefined,
    role: ShopHeaderRole,
): boolean {
    return findShopHeaderRoleBlock(header, role) !== null;
}

export function createShopHeaderRoleBlock(
    role: ShopHeaderRole,
    overrides: ShopHeaderRoleFactoryOptions = {},
): ShopTemplateBlock {
    const href = defaultRoleHref(role);
    const block: ShopTemplateBlock = {
        type: "column",
        ...(href ? { content: { href } } : {}),
        options: defaultRoleOptions(role),
        slots: { content: defaultRoleContent(role) },
    };

    return regenerateShopBlockUuids({
        ...block,
        ...(overrides.content ? {
            content: { ...(block.content ?? {}), ...overrides.content },
        } : {}),
        options: {
            ...(block.options ?? {}),
            ...(overrides.options ?? {}),
            [SHOP_DOCUMENT_ROLE_OPTION]: role,
        },
        ...(overrides.slots ? { slots: overrides.slots } : {}),
    });
}

function patchRoleOptions(
    block: ShopTemplateBlock,
    role: ShopHeaderRole,
    patch: Readonly<Record<string, unknown>>,
): ShopTemplateBlock {
    const options: Record<string, unknown> = {
        ...(block.options ?? {}),
        ...patch,
        [SHOP_DOCUMENT_ROLE_OPTION]: role,
    };

    for (const [key, value] of Object.entries(options)) {
        if (value === undefined) {
            delete options[key];
        }
    }

    return {
        ...block,
        type: "column",
        options,
    };
}

function replaceHeaderGrid(
    header: ShopTemplateBlock,
    grid: ShopTemplateBlock[],
    options: Record<string, unknown> | null = null,
): ShopTemplateBlock {
    return {
        ...header,
        ...(options ? { options } : {}),
        slots: {
            ...(header.slots ?? {}),
            [SHOP_GRID_SLOT_KEY]: grid,
        },
    };
}

function orderedKnownRoleBlocks(
    grid: readonly ShopTemplateBlock[],
    roleOrder: readonly ShopHeaderRole[],
): ShopTemplateBlock[] {
    const order = new Map(roleOrder.map((role, index) => [role, index]));

    return grid
        .filter((block) => shopHeaderRole(block) !== null)
        .slice()
        .sort((left, right) => (
            (order.get(shopHeaderRole(left) as ShopHeaderRole) ?? Number.MAX_SAFE_INTEGER)
            - (order.get(shopHeaderRole(right) as ShopHeaderRole) ?? Number.MAX_SAFE_INTEGER)
        ));
}

function replaceKnownRolePositions(
    grid: readonly ShopTemplateBlock[],
    orderedKnown: readonly ShopTemplateBlock[],
): ShopTemplateBlock[] {
    let knownIndex = 0;

    return grid.map((block) => {
        if (shopHeaderRole(block) === null) {
            return block;
        }

        const replacement = orderedKnown[knownIndex] ?? block;
        knownIndex += 1;

        return replacement;
    });
}

function numericGridOption(block: ShopTemplateBlock, key: "gridH" | "gridW"): number {
    const value = Number(block.options?.[key]);

    return Number.isFinite(value) ? Math.max(1, Math.min(12, Math.round(value))) : 1;
}

function shopHeaderReservedLeadingRows(header: ShopTemplateBlock): number {
    if (header.options?.morrowRegion !== "header") {
        return 0;
    }

    return shopHeaderGrid(header).some((block) => (
        block.options?.[SHOP_DOCUMENT_ROLE_OPTION] === "announcement"
    )) ? 1 : 0;
}

function isCompactMorrowHeader(header: ShopTemplateBlock): boolean {
    return header.options?.morrowRegion === "header"
        && shopHeaderReservedLeadingRows(header) === 1;
}

type MorrowCompactHeaderLayout = {
    placements: ReadonlyMap<ShopHeaderRole, ShopHeaderGridPlacement>;
    roleOrder: readonly ShopHeaderRole[];
};

function distributeMorrowCoreWidths(
    availableWidth: number,
    core: readonly Readonly<{ role: MorrowCompactCoreRole; weight: number }>[],
): number[] {
    if (core.length === 0) {
        return [];
    }

    const totalWeight = core.reduce((sum, item) => sum + item.weight, 0);
    const rawWidths = core.map((item) => availableWidth * item.weight / totalWeight);
    const widths = rawWidths.map((width) => Math.max(1, Math.floor(width)));
    let assigned = widths.reduce((sum, width) => sum + width, 0);
    const remainderOrder = rawWidths
        .map((width, index) => ({ fraction: width - Math.floor(width), index }))
        .sort((left, right) => right.fraction - left.fraction || left.index - right.index);

    for (let offset = 0; assigned < availableWidth; offset += 1) {
        const ranked = remainderOrder[offset % remainderOrder.length];

        if (!ranked) {
            break;
        }

        const current = widths[ranked.index];

        if (current === undefined) {
            continue;
        }

        widths[ranked.index] = current + 1;
        assigned += 1;
    }

    return widths;
}

function resolveMorrowCoreWidthByRole(
    availableWidth: number,
    core: readonly Readonly<{ role: MorrowCompactCoreRole; weight: number }>[],
    fixedSearchWidth?: ShopHeaderSearchWidth,
): ReadonlyMap<MorrowCompactCoreRole, number> {
    const widths = new Map<MorrowCompactCoreRole, number>();
    const hasSearch = core.some(({ role }) => role === "headerSearch");
    const flexibleCore = fixedSearchWidth !== undefined && hasSearch
        ? core.filter(({ role }) => role !== "headerSearch")
        : core;
    let flexibleWidth = availableWidth;

    if (fixedSearchWidth !== undefined && hasSearch) {
        const safeSearchWidth = Math.min(
            fixedSearchWidth,
            availableWidth - flexibleCore.length,
        );

        widths.set("headerSearch", safeSearchWidth);
        flexibleWidth -= safeSearchWidth;
    }

    const distributed = distributeMorrowCoreWidths(flexibleWidth, flexibleCore);

    flexibleCore.forEach(({ role }, index) => {
        widths.set(role, distributed[index] ?? 1);
    });

    return widths;
}

function resolveMorrowCompactHeaderLayout(
    header: ShopTemplateBlock,
    preset: ShopHeaderLayoutPreset,
): MorrowCompactHeaderLayout {
    const present = new Set(shopHeaderRoleBlocks(header).map(({ role }) => role));
    const independentUtilities = MORROW_COMPACT_INDEPENDENT_UTILITY_ROLES.filter((role) => (
        present.has(role)
    ));
    const selectors = MORROW_COMPACT_SELECTOR_ROLES.filter((role) => present.has(role));
    const utilityCellCount = independentUtilities.length + (selectors.length > 0 ? 1 : 0);
    const coreWidth = 12 - utilityCellCount;
    const core = MORROW_COMPACT_CORE_LAYOUT[preset].filter(({ role }) => present.has(role));
    const coreWidths = distributeMorrowCoreWidths(coreWidth, core);
    const placements = new Map<ShopHeaderRole, ShopHeaderGridPlacement>();
    let x = 1;

    core.forEach(({ role }, index) => {
        const width = coreWidths[index] ?? 1;

        placements.set(role, { x, y: 2, w: width, h: 1 });
        x += width;
    });

    x = coreWidth + 1;

    for (const role of independentUtilities) {
        placements.set(role, { x, y: 2, w: 1, h: 1 });
        x += 1;
    }

    for (const role of selectors) {
        placements.set(role, { x, y: 2, w: 1, h: 1 });
    }

    return {
        placements,
        roleOrder: [
            ...core.map(({ role }) => role),
            ...independentUtilities,
            ...selectors,
        ],
    };
}

function resolveMorrowCompactHeaderReflowLayout(
    header: ShopTemplateBlock,
    preset: ShopHeaderLayoutPreset,
    fixedSearchWidth?: ShopHeaderSearchWidth,
): MorrowCompactHeaderLayout {
    const orderedRoles = shopHeaderRoleBlocks(header).map(({ role }) => role);
    const present = new Set(orderedRoles);
    const independentUtilities = MORROW_COMPACT_INDEPENDENT_UTILITY_ROLES.filter((role) => (
        present.has(role)
    ));
    const selectors = MORROW_COMPACT_SELECTOR_ROLES.filter((role) => present.has(role));
    const utilityCellCount = independentUtilities.length + (selectors.length > 0 ? 1 : 0);
    const coreWidth = 12 - utilityCellCount;
    const core = MORROW_COMPACT_CORE_LAYOUT[preset].filter(({ role }) => present.has(role));
    const coreWidthByRole = resolveMorrowCoreWidthByRole(
        coreWidth,
        core,
        fixedSearchWidth,
    );

    const independentSet = new Set<ShopHeaderRole>(independentUtilities);
    const selectorSet = new Set<ShopHeaderRole>(selectors);
    const placements = new Map<ShopHeaderRole, ShopHeaderGridPlacement>();
    let x = 1;

    for (const role of orderedRoles) {
        const coreRole = role as MorrowCompactCoreRole;
        const width = coreWidthByRole.get(coreRole);

        if (width !== undefined) {
            placements.set(role, { x, y: 2, w: width, h: 1 });
            x += width;
            continue;
        }

        if (independentSet.has(role)) {
            placements.set(role, { x, y: 2, w: 1, h: 1 });
            x += 1;
            continue;
        }

        if (selectorSet.has(role) && !placements.has(role)) {
            const placement = { x, y: 2, w: 1, h: 1 };

            for (const selector of selectors) {
                placements.set(selector, placement);
            }

            x += 1;
        }
    }

    return { placements, roleOrder: orderedRoles };
}

function reflowKnownHeaderRoles(
    header: ShopTemplateBlock,
    compactMorrowPreset: ShopHeaderLayoutPreset = "balanced",
    fixedSearchWidth?: ShopHeaderSearchWidth,
): ShopTemplateBlock {
    const grid = [...shopHeaderGrid(header)];

    if (isCompactMorrowHeader(header)) {
        const { placements } = resolveMorrowCompactHeaderReflowLayout(
            header,
            compactMorrowPreset,
            fixedSearchWidth,
        );
        const compactGrid = grid.map((block) => {
            const role = shopHeaderRole(block);
            const placement = role ? placements.get(role) : null;

            return role && placement
                ? patchRoleOptions(block, role, {
                    gridH: placement.h,
                    gridW: placement.w,
                    gridX: placement.x,
                    gridY: placement.y,
                })
                : block;
        });

        return replaceHeaderGrid(
            header,
            compactGrid,
            { ...(header.options ?? {}), minHeight: "124px" },
        );
    }

    let x = 1;
    let y = 1 + shopHeaderReservedLeadingRows(header);
    let lastContentRow = y;
    const nextGrid = grid.map((block) => {
        const role = shopHeaderRole(block);

        if (!role || role === "headerMenu") {
            return block;
        }

        const w = numericGridOption(block, "gridW");
        const h = numericGridOption(block, "gridH");

        if (x + w - 1 > 12) {
            x = 1;
            y += 1;
        }

        const next = patchRoleOptions(block, role, {
            gridH: h,
            gridW: w,
            gridX: x,
            gridY: y,
        });

        x += w;
        lastContentRow = Math.max(lastContentRow, y + h - 1);

        return next;
    }).map((block) => {
        const role = shopHeaderRole(block);

        return role === "headerMenu"
            ? patchRoleOptions(block, role, {
                gridW: 12,
                gridX: 1,
                gridY: lastContentRow + 1,
            })
            : block;
    });

    return replaceHeaderGrid(header, nextGrid);
}

export function insertShopHeaderRole(
    header: ShopTemplateBlock,
    role: ShopHeaderRole,
    toIndex: number = Number.MAX_SAFE_INTEGER,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header) || hasShopHeaderRole(header, role)) {
        return header;
    }

    const resolvedPreset = isCompactMorrowHeader(header)
        ? resolveShopHeaderLayoutPreset(header)
        : "balanced";
    const reflowPreset = resolvedPreset === "custom" ? "balanced" : resolvedPreset;
    const grid = [...shopHeaderGrid(header)];
    const knownIndexes = grid.flatMap((block, index) => (
        shopHeaderRole(block) === null ? [] : [index]
    ));
    const normalizedIndex = Math.max(0, Math.min(Math.round(toIndex), knownIndexes.length));
    const insertionIndex = normalizedIndex < knownIndexes.length
        ? knownIndexes[normalizedIndex] ?? grid.length
        : grid.length;

    grid.splice(insertionIndex, 0, createShopHeaderRoleBlock(role));

    return reflowKnownHeaderRoles(replaceHeaderGrid(header, grid), reflowPreset);
}

export function removeShopHeaderRole(
    header: ShopTemplateBlock,
    role: ShopHeaderRole,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header) || requiredRoleSet.has(role)) {
        return header;
    }

    const resolvedPreset = isCompactMorrowHeader(header)
        ? resolveShopHeaderLayoutPreset(header)
        : "balanced";
    const reflowPreset = resolvedPreset === "custom" ? "balanced" : resolvedPreset;
    const currentGrid = shopHeaderGrid(header);
    const grid = currentGrid.filter((block) => shopHeaderRole(block) !== role);

    if (grid.length === currentGrid.length) {
        return header;
    }

    return reflowKnownHeaderRoles(replaceHeaderGrid(header, grid), reflowPreset);
}

export function moveShopHeaderRole(
    header: ShopTemplateBlock,
    role: ShopHeaderRole,
    toIndex: number,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header)) {
        return header;
    }

    const resolvedPreset = isCompactMorrowHeader(header)
        ? resolveShopHeaderLayoutPreset(header)
        : "balanced";
    const reflowPreset = resolvedPreset === "custom" ? "balanced" : resolvedPreset;
    const grid = [...shopHeaderGrid(header)];
    const known = grid.filter((block) => shopHeaderRole(block) !== null);
    const fromIndex = known.findIndex((block) => shopHeaderRole(block) === role);

    if (fromIndex < 0) {
        return header;
    }

    const normalizedIndex = Math.max(0, Math.min(Math.round(toIndex), known.length - 1));

    if (fromIndex === normalizedIndex) {
        return header;
    }

    const [moved] = known.splice(fromIndex, 1);

    if (!moved) {
        return header;
    }

    known.splice(normalizedIndex, 0, moved);

    return reflowKnownHeaderRoles(replaceHeaderGrid(
        header,
        replaceKnownRolePositions(grid, known),
    ), reflowPreset);
}

export function updateShopHeaderRoleOptions(
    header: ShopTemplateBlock,
    role: ShopHeaderRole,
    patch: Readonly<Record<string, unknown>>,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header)) {
        return header;
    }

    const grid = [...shopHeaderGrid(header)];
    const index = grid.findIndex((block) => shopHeaderRole(block) === role);

    if (index < 0) {
        return header;
    }

    const current = grid[index];

    if (!current) {
        return header;
    }

    grid[index] = patchRoleOptions(current, role, patch);

    return replaceHeaderGrid(header, grid);
}

export function normalizeShopHeaderSearchWidth(value: unknown): ShopHeaderSearchWidth {
    const width = Number(value);

    if (!Number.isFinite(width)) {
        return SHOP_HEADER_SEARCH_WIDTHS.standard;
    }

    return Math.max(
        SHOP_HEADER_SEARCH_WIDTHS.compact,
        Math.min(SHOP_HEADER_SEARCH_WIDTHS.wide, Math.round(width)),
    ) as ShopHeaderSearchWidth;
}

export function resolveShopHeaderSearchSize(
    block: ShopTemplateBlock | null | undefined,
): ShopHeaderSearchSize {
    const width = normalizeShopHeaderSearchWidth(block?.options?.gridW);

    if (width === SHOP_HEADER_SEARCH_WIDTHS.compact) {
        return "compact";
    }

    if (width === SHOP_HEADER_SEARCH_WIDTHS.wide) {
        return "wide";
    }

    return "standard";
}

/**
 * Resizes the authored search column without introducing a parallel layout
 * model. Compact Morrow headers are reflowed on their single header row in
 * the current slots.grid order; other engines retain their existing geometry.
 */
export function resizeShopHeaderSearch(
    header: ShopTemplateBlock,
    targetWidth: number,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header)) {
        return header;
    }

    const search = findShopHeaderRoleBlock(header, "headerSearch");

    if (!search) {
        return header;
    }

    const width = normalizeShopHeaderSearchWidth(targetWidth);

    if (isCompactMorrowHeader(header)) {
        const resolvedPreset = resolveShopHeaderLayoutPreset(header);
        const reflowPreset = resolvedPreset === "custom" ? "balanced" : resolvedPreset;
        const patched = updateShopHeaderRoleOptions(header, "headerSearch", {
            gridH: 1,
            gridW: width,
            gridY: 2,
        });

        return reflowKnownHeaderRoles(patched, reflowPreset, width);
    }

    const currentX = Number(search.options?.gridX);
    const x = Number.isFinite(currentX) ? Math.max(1, Math.round(currentX)) : 1;

    return updateShopHeaderRoleOptions(header, "headerSearch", {
        gridW: width,
        gridX: Math.min(x, 13 - width),
    });
}

export function validateShopHeader(header: ShopTemplateBlock | null | undefined): ShopHeaderValidationIssue[] {
    if (!isShopHeaderRoot(header)) {
        return [{ code: "notHeaderRoot", count: 0, role: null }];
    }

    const counts = new Map<ShopHeaderRole, number>();

    for (const block of shopHeaderGrid(header)) {
        const role = shopHeaderRole(block);

        if (role) {
            counts.set(role, (counts.get(role) ?? 0) + 1);
        }
    }

    const issues: ShopHeaderValidationIssue[] = [];

    for (const role of SHOP_HEADER_REQUIRED_ROLES) {
        const count = counts.get(role) ?? 0;

        if (count === 0) {
            issues.push({ code: "missingRequiredRole", count, role });
        }
    }

    for (const role of SHOP_HEADER_ROLES) {
        const count = counts.get(role) ?? 0;

        if (count > 1) {
            issues.push({ code: "duplicateRole", count, role });
        }
    }

    return issues;
}

export function normalizeShopHeader(header: ShopTemplateBlock): ShopTemplateBlock {
    if (!isShopHeaderRoot(header)) {
        return header;
    }

    const seen = new Set<ShopHeaderRole>();
    const normalizedGrid: ShopTemplateBlock[] = [];

    for (const block of shopHeaderGrid(header)) {
        const role = shopHeaderRole(block);

        if (!role) {
            normalizedGrid.push(block);
            continue;
        }

        if (seen.has(role)) {
            continue;
        }

        seen.add(role);
        normalizedGrid.push(patchRoleOptions(block, role, {}));
    }

    for (const role of SHOP_HEADER_REQUIRED_ROLES) {
        if (!seen.has(role)) {
            normalizedGrid.push(createShopHeaderRoleBlock(role));
        }
    }

    const currentGrid = shopHeaderGrid(header);
    const unchanged = normalizedGrid.length === currentGrid.length
        && normalizedGrid.every((block, index) => (
            block === currentGrid[index]
            || (
                block.type === currentGrid[index]?.type
                && block.options?.[SHOP_DOCUMENT_ROLE_OPTION]
                    === currentGrid[index]?.options?.[SHOP_DOCUMENT_ROLE_OPTION]
            )
        ));

    return unchanged ? header : replaceHeaderGrid(header, normalizedGrid);
}

function placementMatches(block: ShopTemplateBlock, placement: ShopHeaderGridPlacement): boolean {
    return Number(block.options?.gridH) === placement.h
        && Number(block.options?.gridW) === placement.w
        && Number(block.options?.gridX) === placement.x
        && Number(block.options?.gridY) === placement.y;
}

function resolvedPresetPlacement(
    header: ShopTemplateBlock,
    preset: ShopHeaderLayoutPreset,
    role: ShopHeaderRole,
): ShopHeaderGridPlacement {
    const placement = SHOP_HEADER_LAYOUT_PLACEMENTS[preset][role];

    return {
        ...placement,
        y: placement.y + shopHeaderReservedLeadingRows(header),
    };
}

export function resolveShopHeaderLayoutPreset(
    header: ShopTemplateBlock | null | undefined,
): ShopHeaderLayoutPreset | "custom" {
    if (!isShopHeaderRoot(header) || validateShopHeader(header).length > 0) {
        return "custom";
    }

    if (isCompactMorrowHeader(header)) {
        const compact = String(header.options?.minHeight ?? "") === "124px"
            && shopHeaderRoleBlocks(header).every(({ block }) => Number(block.options?.gridY) === 2);

        if (!compact) {
            return "custom";
        }

        for (const preset of SHOP_HEADER_LAYOUT_PRESETS) {
            const { placements } = resolveMorrowCompactHeaderLayout(header, preset);
            const matches = shopHeaderRoleBlocks(header).every(({ block, role }) => (
                placements.has(role)
                && placementMatches(block, placements.get(role) as ShopHeaderGridPlacement)
            ));

            if (matches) {
                return preset;
            }
        }

        return "custom";
    }

    for (const preset of SHOP_HEADER_LAYOUT_PRESETS) {
        const matches = shopHeaderGrid(header).every((block) => {
            const role = shopHeaderRole(block);

            return role === null || placementMatches(
                block,
                resolvedPresetPlacement(header, preset, role),
            );
        });

        if (matches) {
            return preset;
        }
    }

    return "custom";
}

export function applyShopHeaderLayoutPreset(
    header: ShopTemplateBlock,
    preset: ShopHeaderLayoutPreset,
): ShopTemplateBlock {
    if (!isShopHeaderRoot(header)) {
        return header;
    }

    const normalized = normalizeShopHeader(header);
    const grid = [...shopHeaderGrid(normalized)];
    const compactMorrow = isCompactMorrowHeader(normalized);
    const morrowLayout = compactMorrow
        ? resolveMorrowCompactHeaderLayout(normalized, preset)
        : null;
    const patched = grid.map((block) => {
        const role = shopHeaderRole(block);

        if (!role) {
            return block;
        }

        if (morrowLayout) {
            const placement = morrowLayout.placements.get(role);

            if (!placement) {
                return block;
            }

            return patchRoleOptions(block, role, {
                gridH: placement.h,
                gridW: placement.w,
                gridX: placement.x,
                gridY: placement.y,
            });
        }

        const placement = resolvedPresetPlacement(normalized, preset, role);

        return patchRoleOptions(block, role, {
            gridH: placement.h,
            gridW: placement.w,
            gridX: placement.x,
            gridY: placement.y,
        });
    });
    const ordered = orderedKnownRoleBlocks(
        patched,
        morrowLayout?.roleOrder ?? SHOP_HEADER_LAYOUT_ROLE_ORDER[preset],
    );
    const options = {
        ...(normalized.options ?? {}),
        minHeight: compactMorrow
            ? "124px"
            : `${preset === "centeredBrand" || preset === "compact" ? 285 : 197}px`,
    };

    return replaceHeaderGrid(
        normalized,
        replaceKnownRolePositions(patched, ordered),
        options,
    );
}

export function resetShopHeaderLayout(header: ShopTemplateBlock): ShopTemplateBlock {
    return applyShopHeaderLayoutPreset(header, "balanced");
}

export function normalizeShopHeaderUtilityDisplay(
    value: unknown,
    fallback: ShopHeaderUtilityDisplay = "iconLabel",
): ShopHeaderUtilityDisplay {
    return typeof value === "string" && utilityDisplaySet.has(value)
        ? value as ShopHeaderUtilityDisplay
        : fallback;
}

export function resolveShopHeaderUtilityDisplay(
    block: ShopTemplateBlock | null | undefined,
): ShopHeaderUtilityDisplay {
    const role = shopHeaderRole(block);
    const fallback = role ? defaultUtilityDisplay(role) ?? "iconLabel" : "iconLabel";

    return normalizeShopHeaderUtilityDisplay(
        block?.options?.[SHOP_HEADER_UTILITY_DISPLAY_OPTION],
        fallback,
    );
}

export function normalizeShopHeaderAlignment(
    value: unknown,
    fallback: ShopHeaderAlignment = "left",
): ShopHeaderAlignment {
    return typeof value === "string" && alignmentSet.has(value)
        ? value as ShopHeaderAlignment
        : fallback;
}

export function resolveShopHeaderAlignment(
    block: ShopTemplateBlock | null | undefined,
): ShopHeaderAlignment {
    return normalizeShopHeaderAlignment(
        block?.options?.[SHOP_HEADER_ALIGNMENT_OPTION],
        shopHeaderRole(block) === "headerLogo" ? "left" : "center",
    );
}

export function normalizeShopHeaderLogoDisplay(
    value: unknown,
): ShopHeaderLogoDisplay {
    return typeof value === "string" && logoDisplaySet.has(value)
        ? value as ShopHeaderLogoDisplay
        : "markText";
}

export function resolveShopHeaderLogoDisplay(
    block: ShopTemplateBlock | null | undefined,
): ShopHeaderLogoDisplay {
    return normalizeShopHeaderLogoDisplay(
        block?.options?.[SHOP_HEADER_LOGO_DISPLAY_OPTION],
    );
}

function booleanOption(value: unknown, fallback: boolean): boolean {
    if (typeof value === "boolean") return value;
    if (value === "true" || value === 1 || value === "1") return true;
    if (value === "false" || value === 0 || value === "0") return false;

    return fallback;
}

export function shopHeaderBlockVisibleOn(
    block: ShopTemplateBlock | null | undefined,
    viewport: ShopHeaderViewport,
): boolean {
    const role = shopHeaderRole(block);

    if (!role) {
        return true;
    }

    const option = viewport === "desktop"
        ? SHOP_HEADER_SHOW_ON_DESKTOP_OPTION
        : SHOP_HEADER_SHOW_ON_MOBILE_OPTION;

    return booleanOption(
        block?.options?.[option],
        roleDefaultVisibility[role][viewport],
    );
}

export function shopHeaderMenuLocationCandidates(
    header: ShopTemplateBlock | null | undefined,
    viewport: ShopHeaderViewport,
): string[] {
    const menuBlock = findShopHeaderRoleBlock(header, "headerMenu");
    const settings = resolveShopNavigationSettings(menuBlock);

    return siteNavigationMenuLocationCandidates(
        settings,
        viewport,
        SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS[viewport],
    );
}

/**
 * Shop navigation defaults to the populated catalog tree while keeping a
 * dedicated mobile location first. Explicit authored locations still win;
 * blank options normalize back to these safe storefront defaults.
 */
export function resolveShopNavigationSettings(
    navigationBlock: ShopTemplateBlock | null | undefined,
): SiteNavigationSettings {
    return resolveSiteNavigationSettings(navigationBlock, {
        menuLocation: SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS.desktop[0],
        mobileMenuLocation: SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS.mobile[0],
    });
}

/**
 * Chooses a menu by location without cloning or embedding its item tree in the
 * header document. Explicit authored locations take precedence, followed by
 * the populated catalog and the legacy header/main locations.
 */
export function resolveShopHeaderMenu(
    basicData: PublicShopBasicData | null | undefined,
    header: ShopTemplateBlock | null | undefined,
    viewport: ShopHeaderViewport = "desktop",
    languageCode?: string | null,
): PublicShopMenu | null {
    const menus = basicData?.menus ?? [];
    const menuBlock = findShopHeaderRoleBlock(header, "headerMenu");
    const settings = resolveShopNavigationSettings(menuBlock);

    return resolveSiteNavigationMenu(
        menus,
        settings,
        viewport,
        languageCode,
        SHOP_NAVIGATION_MENU_LOCATION_FALLBACKS[viewport],
    );
}
